import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import rentalUnitTemplateService, { 
  RentalUnitTemplate, 
  TemplateFilters,
  CreateTemplateFromUnitData 
} from '../../services/rentalUnitTemplateService';

interface TemplatesState {
  templates: RentalUnitTemplate[];
  popularTemplates: RentalUnitTemplate[];
  selectedTemplate: RentalUnitTemplate | null;
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
  filters: TemplateFilters;
}

const initialState: TemplatesState = {
  templates: [],
  popularTemplates: [],
  selectedTemplate: null,
  isLoading: false,
  isCreating: false,
  error: null,
  pagination: null,
  filters: {
    page: 1,
    limit: 20,
    includeGlobal: true,
    sortBy: 'usageCount',
    sortOrder: 'desc'
  }
};

// Async thunks
export const fetchTemplates = createAsyncThunk(
  'rentalUnitTemplates/fetchTemplates',
  async (filters: TemplateFilters = {}) => {
    const response = await rentalUnitTemplateService.getTemplates(filters);
    return response;
  }
);

export const fetchPopularTemplates = createAsyncThunk(
  'rentalUnitTemplates/fetchPopularTemplates',
  async (limit: number = 10) => {
    const response = await rentalUnitTemplateService.getPopularTemplates(limit);
    return response;
  }
);

export const fetchTemplate = createAsyncThunk(
  'rentalUnitTemplates/fetchTemplate',
  async (id: string) => {
    const response = await rentalUnitTemplateService.getTemplate(id);
    return response;
  }
);

export const createTemplate = createAsyncThunk(
  'rentalUnitTemplates/createTemplate',
  async (templateData: Partial<RentalUnitTemplate>) => {
    const response = await rentalUnitTemplateService.createTemplate(templateData);
    return response;
  }
);

export const createTemplateFromUnit = createAsyncThunk(
  'rentalUnitTemplates/createTemplateFromUnit',
  async ({ unitId, data }: { unitId: string; data: CreateTemplateFromUnitData }) => {
    const response = await rentalUnitTemplateService.createTemplateFromUnit(unitId, data);
    return response;
  }
);

export const updateTemplate = createAsyncThunk(
  'rentalUnitTemplates/updateTemplate',
  async ({ id, data }: { id: string; data: Partial<RentalUnitTemplate> }) => {
    const response = await rentalUnitTemplateService.updateTemplate(id, data);
    return response;
  }
);

export const deleteTemplate = createAsyncThunk(
  'rentalUnitTemplates/deleteTemplate',
  async (id: string) => {
    const response = await rentalUnitTemplateService.deleteTemplate(id);
    return { id };
  }
);

export const recordTemplateUsage = createAsyncThunk(
  'rentalUnitTemplates/recordTemplateUsage',
  async (id: string) => {
    const response = await rentalUnitTemplateService.useTemplate(id);
    return response;
  }
);

export const searchTemplates = createAsyncThunk(
  'rentalUnitTemplates/searchTemplates',
  async ({ searchTerm, filters }: { searchTerm: string; filters?: TemplateFilters }) => {
    const response = await rentalUnitTemplateService.searchTemplates(searchTerm, filters);
    return response;
  }
);

// Slice
const rentalUnitTemplatesSlice = createSlice({
  name: 'rentalUnitTemplates',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TemplateFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch templates';
      });

    // Fetch popular templates
    builder
      .addCase(fetchPopularTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPopularTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.popularTemplates = action.payload.data;
      })
      .addCase(fetchPopularTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch popular templates';
      });

    // Fetch single template
    builder
      .addCase(fetchTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedTemplate = action.payload.data;
      })
      .addCase(fetchTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch template';
      });

    // Create template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.isCreating = false;
        state.templates.unshift(action.payload.data);
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create template';
      });

    // Create template from unit
    builder
      .addCase(createTemplateFromUnit.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createTemplateFromUnit.fulfilled, (state, action) => {
        state.isCreating = false;
        state.templates.unshift(action.payload.data);
      })
      .addCase(createTemplateFromUnit.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.error.message || 'Failed to create template from unit';
      });

    // Update template
    builder
      .addCase(updateTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex(t => t._id === action.payload.data._id);
        if (index !== -1) {
          state.templates[index] = action.payload.data;
        }
        if (state.selectedTemplate?._id === action.payload.data._id) {
          state.selectedTemplate = action.payload.data;
        }
      });

    // Delete template
    builder
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(t => t._id !== action.payload.id);
        if (state.selectedTemplate?._id === action.payload.id) {
          state.selectedTemplate = null;
        }
      });

    // Record template usage
    builder
      .addCase(recordTemplateUsage.fulfilled, (state, action) => {
        const index = state.templates.findIndex(t => t._id === action.payload.data._id);
        if (index !== -1) {
          state.templates[index].usageCount = action.payload.data.usageCount;
        }
      });

    // Search templates
    builder
      .addCase(searchTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.templates = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to search templates';
      });
  }
});

export const { setFilters, clearFilters, clearSelectedTemplate, clearError } = rentalUnitTemplatesSlice.actions;
export default rentalUnitTemplatesSlice.reducer;